/**
 * Environment configuration utility for handling subdomain-based API URLs
 */

export interface EnvironmentConfig {
  apiUrl: string;
  tenant: string;
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Get the API URL based on the current environment and domain
 */
export const getApiUrl = (): string => {
  const hostname = window.location.hostname;
  
  // Handle localhost development
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'http://localhost:8181';
  }
  
  // Handle production domains
  if (hostname === 'phdscanner.com' || hostname === 'www.phdscanner.com') {
    // Main domain - use www subdomain for API
    return 'https://www.phdscanner.com';
  }
  
  // For subdomains (e.g., app.phdscanner.com, oxford.phdscanner.com)
  // Use the exact subdomain for API calls
  const protocol = window.location.protocol;
  return `${protocol}//${hostname}`;
};

/**
 * Get the current subdomain from the hostname
 */
export const getSubdomain = (): string => {
  const hostname = window.location.hostname;
  
  // Handle localhost development
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'app'; // Default tenant for localhost
  }
  
  // Handle root domain (phdscanner.com)
  if (hostname === 'phdscanner.com') {
    return 'www'; // Treat root domain as www subdomain
  }
  
  // Handle www subdomain
  if (hostname === 'www.phdscanner.com') {
    return 'www';
  }
  
  // Extract subdomain from hostname
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[0] !== 'www') {
    return parts[0];
  }
  
  // Default to 'www' if no subdomain found
  return 'www';
};

/**
 * Check if we're on a SaaS subdomain
 */
export const isSaaSSubdomain = (): boolean => {
  const hostname = window.location.hostname;
  
  // Handle localhost development
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    // For development, check if we're on a SaaS route
    return window.location.pathname.startsWith('/saas');
  }
  
  // Handle production domains
  if (hostname === 'phdscanner.com' || hostname === 'www.phdscanner.com') {
    return false; // Main domain - show main portal
  }
  
  // Check if it's a subdomain (e.g., app.phdscanner.com, oxford.phdscanner.com)
  const parts = hostname.split('.');
  if (parts.length >= 3 && parts[0] !== 'www') {
    return true; // Subdomain - show SaaS portal
  }
  
  return false; // Default to main portal
};

/**
 * Get the current environment configuration
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isProduction = process.env.NODE_ENV === 'production';
  const tenant = getSubdomain();
  const apiUrl = getApiUrl();
  
  return {
    apiUrl,
    tenant,
    isDevelopment,
    isProduction
  };
};

/**
 * Get headers for API requests including tenant information
 */
export const getApiHeaders = (): Record<string, string> => {
  const config = getEnvironmentConfig();
  const headers: Record<string, string> = {
    'Accept': 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
  };
  
  // Add tenant header for SaaS endpoints (but not for www/root domain)
  if (config.tenant && config.tenant !== 'www') {
    headers['X-Tenant'] = config.tenant;
  }
  
  return headers;
};

/**
 * Get the correct navigation path for SaaS routes based on environment
 */
export const getSaasPath = (path: string): string => {
  const hostname = window.location.hostname;
  
  // For localhost development, add /saas prefix
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return `/saas${path}`;
  }
  
  // For production subdomains, use the path as-is
  return path;
};

/**
 * Get the correct login path based on environment
 */
export const getLoginPath = (): string => {
  const hostname = window.location.hostname;
  
  // For localhost development, use /saas/login
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return '/saas/login';
  }
  
  // For production subdomains, use /login
  return '/login';
};

/**
 * Get the correct dashboard path based on environment
 */
export const getDashboardPath = (): string => {
  const hostname = window.location.hostname;
  
  // For localhost development, use /saas/dashboard
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return '/saas/dashboard';
  }
  
  // For production subdomains, use /dashboard
  return '/dashboard';
}; 